import React, { useEffect, useState } from "react";
import { Check, ChevronRight, Plus } from "lucide-react";
import { getCardIcon } from "@/helpers/common";
import { formatPlanPrice } from "@/hooks/usePlans";
import { PRO_FEATURES, PRO_INTRO } from "./proBenefits";
import { MONTHLY_PERIOD, YEARLY_PERIOD, keepPeriods, longDay, usageLine, yearlyOption } from "./proUpgradeModel";

// Figma "Paying with" row: the card on file, or "Add payment method".
const PayRow = ({ card, onChange, onAdd }) => (
  <button type="button" className="pro-review__pay" onClick={card ? onChange : onAdd}>
    <span className="pro-review__pay-icon">
      {card ? <img src={getCardIcon(card.Brand)} alt="" /> : <Plus size={20} aria-hidden="true" />}
    </span>
    <span className="pro-review__pay-text">
      <strong>{card ? `${card.Brand} ending ${card.Last4}` : "Add payment method"}</strong>
      {card ? (
        <span>
          Expires {String(card.Exp_Month).padStart(2, "0")}/{String(card.Exp_Year).slice(-2)}
        </span>
      ) : null}
    </span>
    <ChevronRight size={20} aria-hidden="true" />
  </button>
);

// Same copy as the Gitcha Pro page (proBenefits.js).
export const PlanCard = () => (
  <section className="pro-review__plan" aria-label="What Gitcha Pro includes">
    <h3>Pro Member</h3>
    <p>{PRO_INTRO}</p>
    <ul>
      {PRO_FEATURES.map((feature) => (
        <li key={feature.title}>
          <Check size={16} strokeWidth={2.5} aria-hidden="true" />
          <span>
            <b>{feature.title}</b>
            <span>{feature.body}</span>
          </span>
        </li>
      ))}
    </ul>
  </section>
);

const KeepPro = ({ offer, pkg, checkout, onBack, onClose }) => {
  const onTrial = Boolean(offer?.onTrial);
  // Starting the trial is the same step as keeping it: the card goes on a
  // subscription that bills when the trial ends, monthly only.
  const starting = !onTrial && Boolean(offer?.canStartTrial);
  const periods = starting ? ["month"] : keepPeriods(offer);
  const [period, setPeriod] = useState("month");
  const monthlyAmount = offer?.price?.amount ?? pkg?.price?.monthly;
  const monthly = formatPlanPrice(monthlyAmount);
  const yearly = formatPlanPrice(pkg?.price?.yearly);
  const saving = yearlyOption(monthlyAmount, pkg?.price?.yearly);
  const showYearly = periods.includes("year") && Boolean(yearly) && Boolean(saving) && pkg?.price?.source !== "seat";
  const startEnd = starting ? longDay(new Date(Date.now() + (offer?.trialDays || 30) * 24 * 60 * 60 * 1000)) : null;
  const trialEnd = starting ? startEnd : longDay(offer?.trialEndsOn || pkg?.trialEndsAt);
  const usage = onTrial ? usageLine(pkg?.trialUsage) : null;
  const chosen = showYearly ? period : "month";
  const { loadCards } = checkout;

  useEffect(() => {
    loadCards();
  }, [loadCards]);

  const amount = chosen === "year" ? yearly : monthly;
  const interval = chosen === "year" ? "year" : "month";

  const confirm = () => {
    checkout.setError(null);
    if (starting) checkout.startTrial.mutate(checkout.card.Payment_Mode_Id);
    else if (onTrial) checkout.keepPro.mutate(checkout.card.Payment_Mode_Id);
    else checkout.upgradeNow(chosen === "year" ? YEARLY_PERIOD : MONTHLY_PERIOD);
  };

  const options = [
    { key: "month", label: "Monthly", price: monthly ? `${monthly} a month` : null },
    showYearly ? { key: "year", label: saving.label, price: `${yearly} a year` } : null,
  ].filter(Boolean);
  const trialBilling = (onTrial || starting) && trialEnd;
  const dueNote = starting
    ? `Nothing is charged today. ${amount} a month starts on ${trialEnd}. Cancel before then and you are not charged.`
    : trialBilling
      ? `Nothing is charged until ${trialEnd}, then ${amount} a ${interval}.`
      : `${amount} due today, then billed every ${interval}.`;

  return (
    <div className="pro-keep pro-review">
      <h2 className="pro-upgrade__title">
        {starting
          ? `Start your ${offer?.trialDays || 30}-day free trial`
          : onTrial && trialEnd
            ? `Keep Gitcha Pro after\u00a0${trialEnd}`
            : "Upgrade to Gitcha\u00a0Pro"}
      </h2>
      <p className="pro-review__lede">Confirm the details of your new subscription plan.</p>
      {usage ? <p className="pro-keep__usage">{usage}</p> : null}
      <div className="pro-review__grid">
        <PlanCard />
        <div className="pro-review__side">
          <h4>Billing cycle</h4>
          {options.length > 1 ? (
            <div className="pro-keep__options" role="radiogroup" aria-label="Billing">
              {options.map((option) => (
                <label key={option.key} className={`pro-keep__option${chosen === option.key ? " is-on" : ""}`}>
                  <input
                    type="radio"
                    name="pro-keep-period"
                    value={option.key}
                    checked={chosen === option.key}
                    onChange={() => setPeriod(option.key)}
                  />
                  <span className="pro-keep__radio" aria-hidden="true" />
                  <span className="pro-keep__label">{option.label}</span>
                  <span className="pro-keep__price">{option.price}</span>
                </label>
              ))}
            </div>
          ) : (
            <p className="pro-review__cycle">{monthly ? `Monthly (${monthly}/month)` : "Monthly"}</p>
          )}
          <p className="pro-review__note">{dueNote}</p>
          <h4>Paying with</h4>
          <PayRow card={checkout.card} onChange={checkout.changeCard} onAdd={checkout.addCard} />
          <dl className="pro-review__summary">
            <div>
              <dt>Pro Member Plan (Billed {chosen === "year" ? "annually" : "monthly"})</dt>
              <dd>{amount}</dd>
            </div>
            <div>
              <dt>Tax</dt>
              <dd>-</dd>
            </div>
            <div>
              <dt>{trialBilling ? "You'll pay today" : "You'll pay"}</dt>
              <dd>{trialBilling ? "$0.00" : amount}</dd>
            </div>
          </dl>
          {checkout.error ? (
            <p className="pro-upgrade__error" role="alert">
              {checkout.error}
            </p>
          ) : null}
          <div className="pro-review__actions">
            <button type="button" className="pro-review__cancel" onClick={onBack || onClose} disabled={checkout.busy}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary pro-upgrade__cta"
              disabled={!checkout.card || checkout.busy || !monthly}
              onClick={confirm}
            >
              {checkout.busy ? (starting ? "Starting your trial" : "Saving") : starting ? "Start free trial" : "Confirm Subscription"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KeepPro;
